import React, { useState, useEffect } from 'react'
import shortid from 'shortid'
import DatePicker from 'react-datepicker'
import moment from 'moment'
import 'react-datepicker/dist/react-datepicker.css'
import classNames from 'classnames'

import { FormRow, LabelSlot, FieldSlot } from './row'
import Input, { Label } from './input'
import Button from './button'
import ToolTip from './tooltip'
import SelectBoxTypeahead from './select-box-typeahead'
import styles from '../component-styles/campaign-code-generator-rows.module.scss'
import { ValidationChecks } from '../api-client/api-types'
import { isValidInput, prepareInput, hashCode } from '../helpers'
import { MultiSelectBox } from './select-box'
import AddMultiValuesTags from './add-multi-values-tags'
import { makeLinkSecure } from '../helpers/campaign-codes'

const checks = {
  ALL_LOWER_CASE: 'lowercase',
  NO_SPECIAL_CHARS: 'no special characters',
  NO_SPACES: 'no spaces',
}

interface Labels {
  parentID?: string
  icon?: string
  title: string
  items: string[]
}

interface SelectionRowProps {
  optional?: boolean
  isMetaParameter?: boolean
  updateForm: (value: string[]) => void
  value: string[]
  name: string
  list: {
    optionName: string
    optionValue: string
  }[]
  tooltip: string
  id?: string
  last?: boolean
  children?: React.ReactNode
  className?: string
  single?: boolean
  labels?: Labels[]
  defaultError?: boolean
  onValidation?: (valid: boolean) => void
}

export const SelectionRow = ({
  optional,
  isMetaParameter,
  updateForm,
  name,
  list,
  tooltip,
  id,
  last,
  children,
  className,
  value,
  single = true,
  labels,
  defaultError = false,
  onValidation,
}: SelectionRowProps): React.ReactElement => {
  const showLabel: string[] = []
  if (optional) {
    showLabel.push('optional')
  }
  if (isMetaParameter) {
    showLabel.push('meta')
  }
  const useLabel = showLabel.join(' ')

  // useEffect(() => {
  //   if (!single) {
  //     // check if values exists in the list
  //     const vals = list
  //       .filter((s) => {
  //         return value.indexOf(s.optionValue) !== -1
  //       })
  //       .map((a) => a.optionValue)
  //     if (vals.length !== value.length) {
  //       // eslint-disable-next-line
  //         // debugger
  //       // updateForm(vals.length > 0 ? vals : [''])
  //     }
  //   }
  // }, [single, value, list])

  const validateValue = (val: string[]) => {
    if (onValidation && !optional && defaultError) {
      const valueNotSet =
        typeof val === 'undefined' ||
        (Array.isArray(val) && val.join('') === '')
      onValidation(!valueNotSet)
    }
  }

  return (
    <FormRow last={last} className={className}>
      <LabelSlot className={styles.labelSlot}>
        <Label
          id={`select-${hashCode(name || id)}`}
          optional={`${useLabel !== '' ? `(${useLabel})` : ''}`}
        >
          {name}
          {!optional && <span className={styles.asterix}>*</span>}
        </Label>
        <ToolTip showOnRight className={styles.tooltip} message={tooltip} />
      </LabelSlot>
      <FieldSlot>
        {single && (
          <SelectBoxTypeahead
            selectOnBlur
            showClear
            id={`select-${hashCode(name || id)}`}
            value={value.length > 0 ? value[0] : null}
            placeholder="Select value or start typing"
            errorMessage="Please select an existing value or ..."
            showError
            defaultError={defaultError}
            onInputChange={(val: string): void => {
              updateForm([val])
              validateValue([val])
            }}
            items={list
              .concat()
              .sort((p, n) => (p.optionName > n.optionName ? 1 : -1))}
          >
            {children}
          </SelectBoxTypeahead>
        )}
        {!single && (
          <MultiSelectBox
            defaultError={defaultError}
            id={`select-${hashCode(name || id)}`}
            showTags
            typeAhead
            // showNoneWithTypeAhead
            value={value}
            placeholder="Select value(s) or start typing"
            labels={labels}
            showLabelsWithTags={false}
            items={list
              .concat()
              .sort((p, n) => (p.optionName > n.optionName ? 1 : -1))}
            onChange={(val: string[], parentID?: string): void => {
              updateForm(val)
              validateValue(val)
            }}
          />
        )}
        {defaultError && value.join('') === '' && (
          <p className={styles.footNoteError}>
            You must select a {name.toLowerCase()}.
          </p>
        )}
      </FieldSlot>
    </FormRow>
  )
}

interface InputRowProps {
  optional?: boolean
  isMetaParameter?: boolean
  validation?: ValidationChecks[] | undefined | null
  updateForm: (value: string[]) => void
  onValidation?: (valid: boolean) => void
  validationMessage?: string
  value: string[]
  name: string
  tooltip: string
  id?: string
  last?: boolean
  copyFromLabel?: string[]
  className?: string
  single?: boolean
  children?: any
  onInputBlur?: any
  onSetTagStatus?: (tag: string) => null | any
  defaultError?: boolean
  delay?: number
}

export const InputRow = ({
  optional,
  isMetaParameter,
  validation,
  onValidation,
  validationMessage,
  id,
  updateForm,
  value,
  name,
  tooltip,
  last,
  copyFromLabel = [],
  className,
  single = true,
  children = null,
  onInputBlur,
  onSetTagStatus,
  defaultError = false,
  delay,
}: InputRowProps): React.ReactElement => {
  const [error, setError] = useState(defaultError)
  const [inputKey, setInputKey] = useState(shortid.generate())
  const [currentValue, setCurrentValue] = useState(value)

  const errorMessage = !validation
    ? ''
    : validationMessage ||
      `We recommend ${validation
        .filter((item) => item.enabled && checks[item.name])
        .map((item) => {
          return checks[item.name]
        })
        .join(', ')}.`

  useEffect(() => {
    setError(defaultError)
  }, [defaultError])

  useEffect(() => {
    if (currentValue.join('') !== value.join('')) {
      setInputKey(shortid.generate())
      setCurrentValue(value)
    }
    // eslint-disable-next-line
  }, [value])

  const showLabel: string[] = []
  if (optional) {
    showLabel.push('optional')
  }
  if (isMetaParameter) {
    showLabel.push('meta')
  }
  const useLabel = showLabel.join(' ')

  const copyFromLabelText =
    copyFromLabel.length > 0 ? `(copied from ${copyFromLabel.join(', ')})` : ''

  return (
    <FormRow last={last} className={className}>
      <LabelSlot className={styles.labelSlot}>
        <Label
          id={`input-${hashCode(name || id)}`}
          optional={`${
            useLabel !== '' ? `(${useLabel})` : ''
          }${copyFromLabelText}`}
        >
          {name}
          {!optional && <span className={styles.asterix}>*</span>}
        </Label>
        <ToolTip showOnRight className={styles.tooltip} message={tooltip} />
      </LabelSlot>
      <FieldSlot>
        {single && (
          <Input
            delay={delay}
            showClear
            key={inputKey}
            autoCorrect="off"
            autoCapitalize="none"
            autoComplete="off"
            spellCheck="false"
            error={error}
            id={`input-${hashCode(name || id)}`}
            name={`input-${hashCode(name)}`}
            // name={name}
            value={currentValue}
            onBlur={() => {
              if (onInputBlur) {
                if (id === 'landing-page') {
                  const validUrls = currentValue.map((link) =>
                    makeLinkSecure(link),
                  )
                  if (
                    JSON.stringify(validUrls) !== JSON.stringify(currentValue)
                  ) {
                    updateForm(validUrls)
                    onInputBlur(validUrls)
                    return
                  }
                }
                onInputBlur(currentValue)
              }
            }}
            beforeChange={(inputValue: string) => {
              if (isMetaParameter !== true) {
                return prepareInput(inputValue, validation)
              }
              return inputValue
            }}
            onValueChange={(val: string): void => {
              updateForm([val])
              setCurrentValue([val])
              if (validation && onValidation && isMetaParameter !== true) {
                const valid = isValidInput(val, validation)
                onValidation(valid)
                setError(!valid)
              }
            }}
            placeholder={`Type ${name}`}
          />
        )}
        {!single && (
          <AddMultiValuesTags
            defaultError={defaultError}
            initialValue={value}
            enableCommaMultiItems
            className={styles.multiTags}
            type="modal"
            onSetTagStatus={(tag) => {
              if (onSetTagStatus) {
                return onSetTagStatus(tag)
              }
              return null
            }}
            beforeChange={(inputValue: string) => {
              if (validation && onValidation && isMetaParameter !== true) {
                const valid = isValidInput(inputValue, validation)
                onValidation(valid)
                setError(!valid)
              }
              if (isMetaParameter !== true) {
                return prepareInput(inputValue, validation)
              }
              return inputValue
            }}
            placeholder={`Type ${name} value(s)`}
            onBeforeSet={(val: string[]): string[] => {
              if (id === 'landing-page') {
                return val.map((link) => makeLinkSecure(link))
              }
              return val
            }}
            onChange={(val: string[]) => {
              const result = val.filter((item) => item !== '')
              updateForm(result)
              if (validation && onValidation && isMetaParameter !== true) {
                const urlValidLength = !val.some((u) => {
                  return !isValidInput(u, validation)
                })
                onValidation(urlValidLength)
                setError(!urlValidLength)
              }
            }}
          />
        )}
        {errorMessage !== '' && error && (
          <p className={error ? styles.footNoteError : styles.footNote}>
            {currentValue.join('') !== '' ? (
              <>{errorMessage}</>
            ) : (
              <span className={styles.footNoteError}>
                You must enter a {name.toLowerCase()}.
              </span>
            )}
          </p>
        )}
        {children}
      </FieldSlot>
    </FormRow>
  )
}

interface DateRowProps {
  optional?: boolean
  isMetaParameter?: boolean
  updateForm: (value: string[]) => void
  value: null | string
  name: string
  tooltip: string
  id?: string
  last?: boolean
  className?: string
  dateFormat: string
  defaultError?: boolean
  onValidation?: (valid: boolean) => void
}

export const DateRow = ({
  optional,
  isMetaParameter,
  updateForm,
  name,
  tooltip,
  id,
  last,
  className,
  value,
  dateFormat,
  defaultError = false,
  onValidation,
}: DateRowProps): React.ReactElement => {
  // const optionalLabel = optional ? '(optional)' : ''
  // const metaLabel = isMetaParameter ? '(meta)' : ''

  const showLabel: string[] = []
  if (optional) {
    showLabel.push('optional')
  }
  if (isMetaParameter) {
    showLabel.push('meta')
  }
  const useLabel = showLabel.join(' ')

  let dateValue: null | Date = null
  if (value !== null && value !== '') {
    const dateFormatted = moment(value, dateFormat).toDate() // .format(dateFormat)
    dateValue = dateFormatted
  }
  const useDateFormat = dateFormat
    .replace(/Y/gi, 'y')
    .replace(/D/gi, 'd')
    .replace('[Q]', 'QQ')

  const validateValue = (val: string[]) => {
    if (onValidation && !optional && defaultError) {
      const valueNotSet = value === null || value === ''
      onValidation(!valueNotSet)
    }
  }

  return (
    <FormRow last={last} className={className}>
      <LabelSlot className={styles.labelSlot}>
        <Label
          id={`select-${hashCode(name || id)}`}
          optional={`${useLabel !== '' ? `(${useLabel})` : ''}`}
        >
          {name}
          {!optional && <span className={styles.asterix}>*</span>}
        </Label>
        <ToolTip showOnRight className={styles.tooltip} message={tooltip} />
      </LabelSlot>
      <FieldSlot>
        <div className={styles.datePicker}>
          <DatePicker
            className={classNames(styles.dateInput, {
              [styles.dateInputError]:
                (value === null || value === '') && defaultError,
            })}
            dateFormat={useDateFormat}
            showYearPicker={dateFormat === 'YYYY'}
            showMonthYearPicker={dateFormat === 'YYYYMM'}
            showQuarterYearPicker={dateFormat === 'YYYY[q]Q'}
            // format={dateFormat === 'DD/MM/YYYY' ? 'dd/MM/y' : 'MM/dd/y'}
            selected={dateValue}
            onChange={(date): void => {
              // The value can never be a null
              // only empty string is permitted in this case
              let val = ['']
              if (date !== null) {
                const dateF = moment(date.toString()).format(dateFormat)
                val = [dateF]
              }
              updateForm(val)
              validateValue(val)
            }}
          />
          <Button
            className={styles.clearButton}
            type="clear"
            onClick={() => {
              updateForm([''])
              validateValue([''])
            }}
          />
        </div>
        {defaultError && (value === null || value === '') && (
          <p className={styles.footNoteError}>
            You must select a {name.toLowerCase()}.
          </p>
        )}
      </FieldSlot>
    </FormRow>
  )
}
